import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { BlogService } from '../services/blogService';
import { BlogSession, BlogSessionStorage, MultiBlogRequest } from '../models/blog';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { SessionWithAssets } from '../services/assetRetrievalService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`blog-tabpanel-${index}`}
      aria-labelledby={`blog-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const BlogMultiAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [style, setStyle] = useState('');
  const [keyPoints, setKeyPoints] = useState<string[]>([]);
  const [currentKeyPoint, setCurrentKeyPoint] = useState('');
  const [iterations, setIterations] = useState(3);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [includeOutline, setIncludeOutline] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blogPost, setBlogPost] = useState('');
  const [outline, setOutline] = useState('');
  const [sessions, setSessions] = useState<BlogSession[]>([]);

  // Asset retrieval hook for backend data
  const { sessions: backendSessions, loading: backendLoading, refetch: refetchBackendData } = useAssetRetrieval({
    agentType: 'BlogMultiAgentSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true
  });

  // Merge backend sessions with local sessions
  useEffect(() => {
    const stored = localStorage.getItem('blogSessions');
    let localSessions: BlogSession[] = [];

    if (stored) {
      const data: BlogSessionStorage = JSON.parse(stored);
      localSessions = data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      }));
    }

    // Convert backend sessions to BlogSession format
    const convertedBackendSessions: BlogSession[] = backendSessions.map((bs: SessionWithAssets) => {
      // Parse sessionData JSON string to extract blog content
      let sessionData: any = {};
      try {
        if (bs.session.sessionData) {
          sessionData = JSON.parse(bs.session.sessionData);
        }
      } catch (e) {
        console.error('[BlogMultiAgent] Failed to parse sessionData:', e);
      }

      // Assets are already normalized to an array by the service
      return {
        id: bs.session.id,
        topic: sessionData.Topic || bs.session.metadata?.topic || 'Untitled Blog',
        audience: sessionData.Audience || bs.session.metadata?.audience,
        style: sessionData.Style || bs.session.style || bs.session.metadata?.style,
        keyPoints: sessionData.KeyPoints || bs.session.metadata?.keyPoints,
        finalBlogPost: sessionData.FinalBlogPost || bs.assets.find(a => a.assetType === 'blog_post')?.textContent || '',
        outline: sessionData.Outline || bs.assets.find(a => a.assetType === 'outline')?.textContent,
        createdAt: new Date(bs.session.createdAt),
        iterations: bs.session.metadata?.iterations || 3
      };
    });

    // Merge: backend sessions + local-only sessions (filter out duplicates by id)
    const backendIds = new Set(convertedBackendSessions.map(s => s.id));
    const uniqueLocalSessions = localSessions.filter(s => !backendIds.has(s.id));
    const merged = [...convertedBackendSessions, ...uniqueLocalSessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setSessions(merged);
  }, [backendSessions]);

  // Save sessions to localStorage
  const saveSessions = (newSessions: BlogSession[]) => {
    const storage: BlogSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('blogSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleAddKeyPoint = () => {
    if (currentKeyPoint.trim()) {
      setKeyPoints([...keyPoints, currentKeyPoint.trim()]);
      setCurrentKeyPoint('');
    }
  };

  const handleRemoveKeyPoint = (index: number) => {
    setKeyPoints(keyPoints.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a blog topic');
      return;
    }

    setLoading(true);
    setError(null);
    setBlogPost('');
    setOutline('');

    try {
      const request: MultiBlogRequest = {
        topic: topic.trim(),
        audience: audience.trim() || undefined,
        style: style.trim() || undefined,
        keyPoints: keyPoints.length > 0 ? keyPoints : undefined,
        iterations,
        maxTokensPerAgentTurn: maxTokens,
        includeOutline
      };

      const response = await BlogService.generateBlogPost(request);
      
      if (response.finalBlogPost) {
        setBlogPost(response.finalBlogPost);
        setOutline(response.outline || '');
        
        // Save to sessions
        const newSession: BlogSession = {
          id: Date.now().toString(),
          topic: topic.trim(),
          audience: audience.trim() || undefined,
          style: style.trim() || undefined,
          keyPoints: keyPoints.length > 0 ? keyPoints : undefined,
          finalBlogPost: response.finalBlogPost,
          outline: response.outline,
          createdAt: new Date(),
          iterations
        };

        saveSessions([newSession, ...sessions]);
        await refetchBackendData(); // Refresh backend data
        setTabValue(1); // Switch to results tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadSession = (session: BlogSession) => {
    setTopic(session.topic);
    setAudience(session.audience || '');
    setStyle(session.style || '');
    setKeyPoints(session.keyPoints || []);
    setBlogPost(session.finalBlogPost || '');
    setOutline(session.outline || '');
    setIterations(session.iterations || 3);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <ArticleIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Blog Agent' : 'Blog Multi-Agent'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Generate blog posts with AI' : 'Generate comprehensive blog posts using multiple AI agents working together'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 2 }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab 
            label="Generate" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="Results" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="History" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 2, sm: 3, md: 4 }, 
            maxWidth: 600, 
            mx: 'auto' 
          }}>
            <TextField
              fullWidth
              label={isMobile ? 'Topic' : 'Blog Topic'}
              placeholder={isMobile ? 'e.g., Renewable Energy' : 'e.g., The Future of Renewable Energy'}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <TextField
              fullWidth
              label={isMobile ? 'Audience' : 'Target Audience'}
              placeholder={isMobile ? 'e.g., Tech enthusiasts' : 'e.g., Technology enthusiasts, business professionals'}
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <TextField
              fullWidth
              label={isMobile ? 'Style' : 'Writing Style'}
              placeholder={isMobile ? 'e.g., Professional' : 'e.g., Professional, conversational, technical'}
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.125rem' }
                }}
              >
                {isMobile ? 'Key Points' : 'Key Points to Cover'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={isMobile ? 'Add point' : 'Add a key point'}
                  value={currentKeyPoint}
                  onChange={(e) => setCurrentKeyPoint(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddKeyPoint();
                    }
                  }}
                  sx={{ borderRadius: 1 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddKeyPoint}
                  startIcon={!isMobile && <AddIcon />}
                  sx={{ 
                    borderRadius: 1.5, 
                    minWidth: { xs: 60, sm: 100 },
                    minHeight: 44
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {keyPoints.map((point, index) => (
                  <Chip
                    key={index}
                    label={point}
                    onDelete={() => handleRemoveKeyPoint(index)}
                    deleteIcon={<CloseIcon />}
                    sx={{ 
                      borderRadius: 1.5,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              <Box>
                <Typography 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: '0.875rem', sm: '0.95rem' }, 
                    mb: 2 
                  }}
                >
                  {isMobile ? 'Iterations' : 'Agent Iterations'}: {iterations}
                </Typography>
                <Slider
                  value={iterations}
                  onChange={(_, value) => setIterations(value as number)}
                  min={1}
                  max={5}
                  marks
                  valueLabelDisplay="auto"
                  sx={{ mx: 1 }}
                />
              </Box>

              <Box>
                <Typography 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: '0.875rem', sm: '0.95rem' }, 
                    mb: 2 
                  }}
                >
                  {isMobile ? 'Tokens' : 'Max Tokens per Agent'}: {maxTokens}
                </Typography>
                <Slider
                  value={maxTokens}
                  onChange={(_, value) => setMaxTokens(value as number)}
                  min={512}
                  max={4096}
                  step={256}
                  valueLabelDisplay="auto"
                  sx={{ mx: 1 }}
                />
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={includeOutline}
                  onChange={(e) => setIncludeOutline(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Include outline' : 'Include blog outline'}
                </Typography>
              }
            />

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              onClick={handleGenerate}
              disabled={loading || !topic.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && null)}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                mt: 2,
                minHeight: 44
              }}
            >
              {loading ? (isMobile ? 'Generating...' : 'Generating...') : (isMobile ? 'Generate' : 'Generate Blog Post')}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {blogPost ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {outline && (
                <Card 
                  variant="outlined"
                  sx={{ 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.125rem' }
                        }}
                      >
                        {isMobile ? 'Outline' : 'Blog Outline'}
                      </Typography>
                      <Box>
                        {!isMobile && (
                          <Tooltip title="Copy outline">
                            <IconButton onClick={() => handleCopy(outline)} size="small" sx={{ minWidth: 44, minHeight: 44 }}>
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={isMobile ? "Copy" : "Download as Markdown"}>
                          <IconButton 
                            onClick={() => isMobile ? handleCopy(outline) : handleDownload(outline, `${topic.replace(/\s+/g, '-')}-outline.md`)} 
                            size="small"
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            {isMobile ? <CopyIcon /> : <DownloadIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: { xs: 2, sm: 3 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: 'text.primary',
                          fontWeight: 600,
                          mt: 2,
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          '&:first-of-type': { mt: 0 }
                        },
                        '& p': {
                          color: 'text.primary',
                          lineHeight: 1.6,
                          mb: 1,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        },
                        '& ul, & ol': {
                          color: 'text.primary',
                          pl: 2
                        },
                        '& li': {
                          mb: 0.5,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      <ReactMarkdown>{outline}</ReactMarkdown>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Card 
                sx={{ 
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '1rem', sm: '1.125rem' }
                      }}
                    >
                      {isMobile ? 'Blog Post' : 'Final Blog Post'}
                    </Typography>
                    <Box>
                      {!isMobile && (
                        <Tooltip title="Copy blog post">
                          <IconButton onClick={() => handleCopy(blogPost)} size="small" sx={{ minWidth: 44, minHeight: 44 }}>
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={isMobile ? "Copy" : "Download as Markdown"}>
                        <IconButton 
                          onClick={() => isMobile ? handleCopy(blogPost) : handleDownload(blogPost, `${topic.replace(/\s+/g, '-')}-blog.md`)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          {isMobile ? <CopyIcon /> : <DownloadIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: { xs: 2, sm: 3, md: 4 },
                      '& h1': {
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 2,
                        mt: 0
                      },
                      '& h2': {
                        fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                        fontWeight: 600,
                        color: 'text.primary',
                        mt: { xs: 2, sm: 2.5, md: 3 },
                        mb: { xs: 1.5, sm: 2 }
                      },
                      '& h3': {
                        fontSize: { xs: '1.125rem', sm: '1.187rem', md: '1.25rem' },
                        fontWeight: 600,
                        color: 'text.primary',
                        mt: { xs: 2, sm: 2.25, md: 2.5 },
                        mb: { xs: 1, sm: 1.25, md: 1.5 }
                      },
                      '& h4, & h5, & h6': {
                        fontWeight: 600,
                        color: 'text.primary',
                        mt: 2,
                        mb: 1,
                        fontSize: { xs: '1rem', sm: '1.05rem' }
                      },
                      '& p': {
                        color: 'text.primary',
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                        lineHeight: 1.7,
                        mb: 2
                      },
                      '& ul, & ol': {
                        color: 'text.primary',
                        pl: 2,
                        mb: 2
                      },
                      '& li': {
                        mb: 0.5,
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                        lineHeight: 1.6
                      },
                      '& blockquote': {
                        borderLeft: 4,
                        borderColor: 'primary.main',
                        pl: 2,
                        ml: 0,
                        my: 2,
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' }
                      },
                      '& code': {
                        backgroundColor: 'action.hover',
                        color: 'text.primary',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
                      },
                      '& pre': {
                        backgroundColor: 'action.hover',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 1,
                        overflow: 'auto'
                      }
                    }}
                  >
                    <ReactMarkdown>{blogPost}</ReactMarkdown>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6, md: 8 } }}>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No blog yet' : 'No blog post generated yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' }
                }}
              >
                {isMobile ? 'Go to Generate to create one' : 'Go to the Generate tab to create a blog post'}
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ py: { xs: 1.5, sm: 2 } }}
                >
                  <ListItemText
                    primary={session.topic}
                    secondary={
                      isMobile ? (
                        `Created: ${session.createdAt.toLocaleDateString()}`
                      ) : (
                        <>
                          {session.audience && `Audience: ${session.audience} • `}
                          {session.style && `Style: ${session.style} • `}
                          Created: {session.createdAt.toLocaleDateString()}
                        </>
                      )
                    }
                    onClick={() => handleLoadSession(session)}
                    sx={{ cursor: 'pointer' }}
                    primaryTypographyProps={{
                      sx: { fontSize: { xs: '0.95rem', sm: '1rem' } }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: { xs: '0.8rem', sm: '0.875rem' } }
                    }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={isMobile ? "" : "Delete"}>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteSession(session.id)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6, md: 8 } }}>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No history' : 'No blog posts in history'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' }
                }}
              >
                {isMobile ? 'Blogs will appear here' : 'Generated blog posts will appear here'}
              </Typography>
            </Box>
          )}
        </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default BlogMultiAgent;